/**
 * Pure helpers for `CreateGroupDialog`, kept outside the component file
 * (react-refresh wants component-only modules) so they are unit-testable
 * without mounting MUI.
 */

/**
 * Routes a 409 conflict to the field it belongs to. Both backend messages
 * embed a user-supplied group name (`Group name "X" already exists` /
 * `Group "X" already has the same category rule` — see
 * GroupsService), so the routing must NOT keyword-match anything that can
 * appear inside the quoted name. The duplicate-categoryRule message is the
 * only one that ENDS with the "already has the same category rule" phrase
 * (the duplicate-name one always ends with `" already exists`), so anchor
 * on that suffix; everything else — duplicate name or unrecognized copy —
 * defaults to the name field.
 *
 * The feature-flag conflict ("Cannot change feature flags while the group is
 * on/panicked") belongs on no field either — painting it under Name shows a
 * name-validation error whose text talks about feature flags, with the flags
 * section collapsed and nothing wrong with the name. Same for the two
 * External Live Signals conflicts (`liveSignalSources`/`liveSignalTuning`
 * while running) — they route to `LiveSignalsSection`, not any text field.
 */
export function conflict409Field(
  message: string
): 'category' | 'name' | 'general' {
  if (/already has the same category\s*rule\s*$/i.test(message))
    return 'category';
  // Anchored, not a keyword match: the duplicate-name message embeds the
  // user's group name, so a bare /feature flags/i would route
  // `Group name "feature flags" already exists` away from the Name field.
  if (/^Cannot change feature flags/i.test(message)) return 'general';
  // Exact backend copy: "Cannot change liveSignalSources/liveSignalTuning
  // while the group is on/panicked — stop it first".
  if (/^Cannot change liveSignalSources\b/i.test(message)) return 'general';
  if (/^Cannot change liveSignalTuning\b/i.test(message)) return 'general';
  // Changing either venue account under live pods (GroupsService.update):
  // `Cannot change kalshiAccountId while the group is on/panicked`. The select
  // is already locked in that state, so this only arrives when the lock and the
  // pod state disagreed — it is not a field the operator can fix here.
  if (/^Cannot change \w+AccountId\b/i.test(message)) return 'general';
  // Every venue-account refusal from the START path is prefixed with the
  // group's OBJECT ID — `Group <id> cannot start: kalshi account 'x' is
  // disabled` / `... is not registered in venue_accounts` / `... not mounted` /
  // the maxQuotedMarkets capacity refusal — as is the missing-account refusal
  // (`Group <id> has no kalshi account — pick one before starting`) and the
  // cancel-scope one. Anchoring on that prefix is what keeps them apart from
  // the duplicate-name message, which is `Group name "X" already exists`: an
  // unanchored /account/i would route `Group name "Kalshi main account"
  // already exists` away from the Name field, which is where it belongs.
  if (/^Group \S+ cannot (start|cancel)\b/i.test(message)) return 'general';
  if (/^Group \S+ has no \S+( and \S+)? account\b/i.test(message))
    return 'general';
  return 'name';
}

/**
 * Collision-proof select value/key for a category bucket — a plain
 * string-join could collide (e.g. `a b`+`c` vs `a`+`b c`).
 */
export const categoryKey = (
  category: string,
  subcategory: string | null
): string => JSON.stringify([category, subcategory ?? null]);
